import httpService from 'services/httpService';
import CryptoJS from 'crypto-js';

const CHUNK_SIZE = 512 * 1024; // 512KB chunks

// Compute SHA256 hash of file
const computeFileHash = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const wordArray = CryptoJS.lib.WordArray.create(reader.result);
      const hash = CryptoJS.SHA256(wordArray).toString();
      resolve(hash);
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
};

// Send first chunk to backend with file metadata
const sendFirstChunk = async (activityId, file, chunk, hash, totalChunks, studentEmail, studentName) => {
  const formData = new FormData();
  formData.append("file", chunk);
  formData.append("file_name", file.name);
  formData.append("total_chunks", totalChunks);
  formData.append("hash", hash);
  formData.append("chunk_index", 0);

  const response = await httpService.post(
    `/otherlabs/activity/${activityId}/other_submission_files/?stud_id=${encodeURIComponent(studentEmail)}&username=${encodeURIComponent(studentName)}`,
    formData,
    { timeout: 120000 }
  );

  console.log(`[CHUNKED_UPLOAD] First chunk response:`, response.data);

  if (!response.data.file_id) {
    console.error(`[CHUNKED_UPLOAD] ERROR: No file_id in response!`, response.data);
    throw new Error('No file_id received from server');
  }

  // Store record_id if returned (for single-chunk files)
  const result = { fileId: response.data.file_id };
  if (response.data.record_id) {
    result.recordId = response.data.record_id;
  }

  return result;
};

// Send subsequent chunks
const sendNextChunk = async (activityId, fileId, chunkIndex, chunk, studentEmail, studentName) => {
  const formData = new FormData();
  formData.append("file", chunk);
  formData.append("file_id", fileId);
  formData.append("chunk_index", chunkIndex);

  console.log(`[CHUNKED_UPLOAD] sendNextChunk called with fileId: ${fileId}, chunkIndex: ${chunkIndex}`);

  const response = await httpService.post(
    `/otherlabs/activity/${activityId}/other_submission_files/?stud_id=${encodeURIComponent(studentEmail)}&username=${encodeURIComponent(studentName)}`,
    formData,
    { timeout: 120000 }
  );

  console.log(`[CHUNKED_UPLOAD] Chunk ${chunkIndex} response:`, response.data);
  return response;
};

// Main chunked upload function
export const uploadArchiveInChunks = async (activityId, file, studentEmail, studentName, onProgress) => {
  try {
    // Debug file object
    console.log(`[CHUNKED_UPLOAD] File object:`, file);
    console.log(`[CHUNKED_UPLOAD] File name: ${file.name}`);
    console.log(`[CHUNKED_UPLOAD] File type: ${file.type}`);
    console.log(`[CHUNKED_UPLOAD] File size property: ${file.size}`);
    console.log(`[CHUNKED_UPLOAD] CHUNK_SIZE constant: ${CHUNK_SIZE}`);

    // Calculate chunks (handles files <512KB correctly - will be 1 chunk)
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    console.log(`[CHUNKED_UPLOAD] Calculation: Math.ceil(${file.size} / ${CHUNK_SIZE}) = ${totalChunks}`);
    console.log(`[CHUNKED_UPLOAD] File size: ${file.size} bytes, Total chunks: ${totalChunks}`);

    // Compute file hash
    if (onProgress) onProgress('Computing file hash...', 0);
    const hash = await computeFileHash(file);
    console.log(`[CHUNKED_UPLOAD] Computed hash: ${hash}`);

    // Send first chunk with metadata
    if (onProgress) onProgress('Starting upload...', 0);
    const firstChunk = file.slice(0, CHUNK_SIZE);
    console.log(`[CHUNKED_UPLOAD] Sending first chunk, size: ${firstChunk.size} bytes`);
    const firstChunkResult = await sendFirstChunk(activityId, file, firstChunk, hash, totalChunks, studentEmail, studentName);
    const fileId = firstChunkResult.fileId;
    let recordId = firstChunkResult.recordId || null;
    console.log(`[CHUNKED_UPLOAD] First chunk sent, file_id: ${fileId}, record_id: ${recordId}`);

    // Send remaining chunks (if any)
    for (let i = 1; i < totalChunks; i++) {
      const start = i * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const chunk = file.slice(start, end);

      console.log(`[CHUNKED_UPLOAD] Sending chunk ${i}/${totalChunks - 1}, size: ${chunk.size} bytes`);

      if (onProgress) {
        const progress = ((i + 1) / totalChunks) * 100;
        onProgress(`Uploading...`, progress);
      }

      const response = await sendNextChunk(activityId, fileId, i, chunk, studentEmail, studentName);
      console.log(`[CHUNKED_UPLOAD] Chunk ${i} sent successfully`);

      // Check if this was the last chunk and we got a record_id
      if (response.data.record_id) {
        recordId = response.data.record_id;
        console.log(`[CHUNKED_UPLOAD] File processing complete. Record ID: ${recordId}`);
      }
    }

    console.log(`[CHUNKED_UPLOAD] All chunks sent, processing...`);
    if (onProgress) onProgress('Upload completed! Processing file...', 100);

    return { success: true, fileId, recordId };
  } catch (error) {
    console.error('[CHUNKED_UPLOAD] Upload failed:', error);
    throw error;
  }
};
